import { useState, useEffect, useRef } from 'react';
import type { FC, FormEvent, ChangeEvent } from 'react';
import type { ToneOption, FormatOption } from '../../services/apiService';

interface SpecialFormData {
  restaurantUrl: string;
  context?: string;
  includeEmojis: boolean;
  customImage?: File;
  useCustomImage: boolean;
  useDefaultImage: boolean;
  previewUrl?: string; // For image preview
  tone: ToneOption;
  format: FormatOption;
}

interface SpecialFormProps {
  onSubmit: (data: SpecialFormData) => Promise<void>;
  isLoading: boolean;
}

// Loading dots animation component
const LoadingDots: FC = () => {
  const [dots, setDots] = useState('');
  const intervalRef = useRef<number | null>(null);
  
  useEffect(() => {
    // Animate the dots
    intervalRef.current = window.setInterval(() => {
      setDots(prevDots => {
        if (prevDots.length >= 3) return '';
        return prevDots + '.';
      });
    }, 400);
    
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);
  
  return (
    <span className="inline-block min-w-[24px] text-left">{dots}</span>
  );
};

export const SpecialForm: FC<SpecialFormProps> = ({ onSubmit, isLoading }) => {
  const [formData, setFormData] = useState<SpecialFormData>({
    restaurantUrl: '',
    context: '',
    includeEmojis: true,
    useCustomImage: false,
    useDefaultImage: true, // Default to using Bubbles AI generated image
    tone: 'casual', // Default tone
    format: 'paragraphs', // Default format
  });

  const [urlError, setUrlError] = useState<string | null>(null);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === 'checkbox';
    
    // Handle checkbox changes
    if (isCheckbox) {
      const checked = (e.target as HTMLInputElement).checked;
      
      // Handle image option toggles
      if (name === 'useCustomImage') {
        if (checked) {
          // If turning on useCustomImage, turn off useDefaultImage
          setFormData(prev => ({
            ...prev,
            useCustomImage: checked,
            useDefaultImage: false
          }));
        } else {
          // If turning off useCustomImage, clear the customImage
          setFormData(prev => ({
            ...prev,
            useCustomImage: false,
            customImage: undefined
          }));
        }
      } else if (name === 'useDefaultImage') {
        if (checked) {
          // If turning on useDefaultImage, turn off useCustomImage
          setFormData(prev => ({
            ...prev,
            useDefaultImage: true,
            useCustomImage: false,
            customImage: undefined
          }));
        } else {
          // Just turn off useDefaultImage
          setFormData(prev => ({
            ...prev,
            useDefaultImage: false
          }));
        }
      } else {
        // Handle other checkboxes normally
        setFormData(prev => ({
          ...prev,
          [name]: checked,
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };
  
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (5MB limit)
      const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSizeInBytes) {
        alert('Image is too large. Please select an image under 5MB.');
        // Reset the file input
        e.target.value = '';
        return;
      }
      
      // Create a preview URL for the selected image
      const previewUrl = URL.createObjectURL(file);
      
      setFormData(prev => ({
        ...prev,
        customImage: file,
        useCustomImage: true,
        previewUrl
      }));
    }
  };
  
  // Clean up object URLs when component unmounts or when preview changes
  useEffect(() => {
    return () => {
      // Revoke the object URL to avoid memory leaks
      if (formData.previewUrl) {
        URL.revokeObjectURL(formData.previewUrl);
      }
    };
  }, [formData.previewUrl]);

  function isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isValidUrl(formData.restaurantUrl)) {
      setUrlError('Please enter a valid website URL including https://');
      return;
    }
    setUrlError(null);
    await onSubmit(formData);
  };


  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="restaurantUrl" className="block text-sm font-medium text-gray-700 mb-1">
          Restaurant Website URL
        </label>
        <input
          type="text"
          id="restaurantUrl"
          name="restaurantUrl"
          value={formData.restaurantUrl}
          onChange={handleInputChange}
          placeholder="https://yourrestaurant.com"
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          required
          autoComplete="off"
        />
        {urlError && (
          <div className="mt-2 text-sm text-red-600" role="alert">
            {urlError}
          </div>
        )}
      </div>

      <div>
        <label htmlFor="context" className="block text-sm font-medium text-gray-700 mb-1">
          Optional Context (e.g., focus ingredient, discount)
        </label>
        <textarea
          id="context"
          name="context"
          value={formData.context}
          onChange={handleInputChange}
          placeholder="e.g., 'Featuring fresh basil from the garden' or '20% off this week'"
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      <div className="flex items-center justify-between mb-6">
        <span className="text-gray-700">Include Emojis? 🥳</span>
        <label htmlFor="emoji-toggle" className="inline-flex relative items-center cursor-pointer">
          <input 
            type="checkbox" 
            id="emoji-toggle" 
            name="includeEmojis"
            className="sr-only peer" 
            checked={formData.includeEmojis}
            onChange={handleInputChange}
          />
          <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
        </label>
      </div>
      
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">Ad Tone</label>
        <div className="flex flex-wrap gap-4">
          <label className={`flex items-center p-3 rounded-lg border ${formData.tone === 'casual' ? 'border-primary-500 bg-primary-50' : 'border-gray-200'} cursor-pointer transition-all`}>
            <input
              type="radio"
              name="tone"
              value="casual"
              checked={formData.tone === 'casual'}
              onChange={handleInputChange}
              className="sr-only"
            />
            <div className="flex flex-col">
              <span className="font-medium">Casual</span>
              <span className="text-xs text-gray-500">Friendly, relaxed style</span>
            </div>
          </label>
          
          <label className={`flex items-center p-3 rounded-lg border ${formData.tone === 'formal' ? 'border-primary-500 bg-primary-50' : 'border-gray-200'} cursor-pointer transition-all`}>
            <input
              type="radio"
              name="tone"
              value="formal"
              checked={formData.tone === 'formal'}
              onChange={handleInputChange}
              className="sr-only"
            />
            <div className="flex flex-col">
              <span className="font-medium">Formal</span>
              <span className="text-xs text-gray-500">Elegant, sophisticated style</span>
            </div>
          </label>
          
          <label className={`flex items-center p-3 rounded-lg border ${formData.tone === 'hip' ? 'border-primary-500 bg-primary-50' : 'border-gray-200'} cursor-pointer transition-all`}>
            <input
              type="radio"
              name="tone"
              value="hip"
              checked={formData.tone === 'hip'}
              onChange={handleInputChange}
              className="sr-only"
            />
            <div className="flex flex-col">
              <span className="font-medium">Hip</span>
              <span className="text-xs text-gray-500">Trendy, modern style</span>
            </div>
          </label>
        </div>
      </div>
      
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">Output Format</label>
        <div className="flex flex-wrap gap-4">
          <label className={`flex items-center p-3 rounded-lg border ${formData.format === 'menu' ? 'border-primary-500 bg-primary-50' : 'border-gray-200'} cursor-pointer transition-all`}>
            <input
              type="radio"
              name="format"
              value="menu"
              checked={formData.format === 'menu'}
              onChange={handleInputChange}
              className="sr-only"
            />
            <div className="flex flex-col">
              <span className="font-medium">Menu Style</span>
              <span className="text-xs text-gray-500">Structured with bullets & sections</span>
            </div>
          </label>
          
          <label className={`flex items-center p-3 rounded-lg border ${formData.format === 'intro-bullets' ? 'border-primary-500 bg-primary-50' : 'border-gray-200'} cursor-pointer transition-all`}>
            <input
              type="radio"
              name="format"
              value="intro-bullets"
              checked={formData.format === 'intro-bullets'}
              onChange={handleInputChange}
              className="sr-only"
            />
            <div className="flex flex-col">
              <span className="font-medium">Intro + Bullets</span>
              <span className="text-xs text-gray-500">Paragraph intro with bullet points</span>
            </div>
          </label>
          
          <label className={`flex items-center p-3 rounded-lg border ${formData.format === 'paragraphs' ? 'border-primary-500 bg-primary-50' : 'border-gray-200'} cursor-pointer transition-all`}>
            <input
              type="radio"
              name="format"
              value="paragraphs"
              checked={formData.format === 'paragraphs'}
              onChange={handleInputChange}
              className="sr-only"
            />
            <div className="flex flex-col">
              <span className="font-medium">Paragraphs</span>
              <span className="text-xs text-gray-500">Traditional paragraph format</span>
            </div>
          </label>
        </div>
      </div>
      
      <div className="mb-6">
        <div className="flex flex-col space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-700 text-sm font-medium">Image Options</span>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Upload Image Option */}
            <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200">
              <span className="text-gray-700">Upload Image</span>
              <label htmlFor="image-toggle" className="inline-flex relative items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  id="image-toggle" 
                  name="useCustomImage"
                  className="sr-only peer" 
                  checked={formData.useCustomImage}
                  onChange={handleInputChange}
                />
                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>
            
            {/* Bubbles AI Generated Image Option */}
            <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200">
              <span className="text-gray-700">AI Generated</span>
              <label htmlFor="default-image-toggle" className="inline-flex relative items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  id="default-image-toggle" 
                  name="useDefaultImage"
                  className="sr-only peer" 
                  checked={formData.useDefaultImage}
                  onChange={handleInputChange}
                />
                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>
          
          {formData.useCustomImage && (
            <div className="mt-2">
              <label htmlFor="customImage" className="block text-sm font-medium text-gray-700 mb-1">
                Select Image
              </label>
              <input
                type="file"
                id="customImage"
                name="customImage"
                accept="image/*"
                onChange={handleFileChange}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                required={formData.useCustomImage}
              />
              <p className="mt-1 text-xs text-gray-500">Recommended: Square image (1:1 ratio), max 5MB</p>
              
              {formData.previewUrl && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-gray-700 mb-1">Preview:</p>
                  <div className="relative w-full h-40 bg-gray-100 rounded-md overflow-hidden">
                    <img 
                      src={formData.previewUrl} 
                      alt="Preview" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="pt-2">
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-md transition duration-300 flex items-center justify-center"
        >
          {isLoading ? (
            <span className="flex items-center">
              Generating<LoadingDots />
            </span>
          ) : (
            <span>Generate Special</span>
          )}
        </button>
      </div>
    </form>
  );
};
